import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type { EmployeeFaltaDto, EmployeeFaltaInput, EmployeeFaltaListItem } from './types';

/**
 * CRUD aninhado em um Colaborador. `employeeId` fecha no escopo.
 * Mesma estrutura de `createEmployeeTaxasApi`.
 *
 * Id do filho é `dataFalta` (ISO `YYYY-MM-DD`) — string.
 */
export function createEmployeeFaltasApi(
  employeeId: number,
): CrudApi<EmployeeFaltaListItem, EmployeeFaltaDto, EmployeeFaltaInput, string> {
  const BASE = `/v1/employees/${employeeId}/faltas`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<EmployeeFaltaListItem>> {
      const { data } = await api.get<{
        data: Omit<EmployeeFaltaListItem, 'id'>[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      const normalized = normalizeListEnvelope(data);
      return {
        ...normalized,
        data: normalized.data.map((r) => ({ ...r, id: r.dataFalta })),
      };
    },

    async get(dataFalta: string): Promise<EmployeeFaltaDto> {
      const { data } = await api.get<{ data: EmployeeFaltaDto }>(`${BASE}/${dataFalta}`);
      return data.data;
    },

    async create(input: EmployeeFaltaInput): Promise<EmployeeFaltaDto> {
      const { data } = await api.post<{ data: EmployeeFaltaDto }>(BASE, input);
      return data.data;
    },

    async update(dataFalta: string, input: EmployeeFaltaInput): Promise<EmployeeFaltaDto> {
      const { data } = await api.put<{ data: EmployeeFaltaDto }>(`${BASE}/${dataFalta}`, input);
      return data.data;
    },

    async remove(dataFalta: string): Promise<void> {
      await api.delete(`${BASE}/${dataFalta}`);
    },
  };
}
